import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Paper,
  ButtonBase,
  Avatar,
  Badge,
  Tooltip,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import AccountCircle from "@material-ui/icons/AccountCircle";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import PaletteOutlinedIcon from "@material-ui/icons/PaletteOutlined";
import CloseIcon from "@material-ui/icons/Close";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";
import { BrandOrbitIcon } from "../components/CrmIcons";

const drawerWidth = 232;
const compactDrawerWidth = 84;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    [theme.breakpoints.down("sm")]: {
      height: "calc(100vh - 56px)",
    },
  },
  toolbar: {
    paddingRight: 18,
    minHeight: 52,
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 14px",
    minHeight: "76px",
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  brandMark: {
    width: 48,
    height: 48,
    borderRadius: 16,
    display: "grid",
    placeItems: "center",
    background: "rgba(255,255,255,.08)",
    color: "#a99aff",
    fontWeight: 800,
    letterSpacing: "-0.04em",
  },
  brandText: {
    fontWeight: 800,
    fontSize: "0.9rem",
    color: "#fff",
    letterSpacing: "-0.01em",
    whiteSpace: "nowrap",
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 2px 12px rgba(15,23,42,.04)",
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("xs")]: {
      marginLeft: 0,
      width: "100%",
    },
  },
  appBarCompact: {
    marginLeft: compactDrawerWidth,
    width: `calc(100% - ${compactDrawerWidth}px)`,
    [theme.breakpoints.down("xs")]: {
      marginLeft: 0,
      width: "100%",
    },
  },
  menuButton: {
    marginRight: 36,
    color: theme.palette.text.primary,
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flexGrow: 1,
    color: theme.palette.text.primary,
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    background: "linear-gradient(180deg, #1d1239 0%, #160d31 100%)",
    "--crm-nav-foreground": "#ffffff",
    borderRight: "1px solid rgba(255,255,255,.08)",
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
    boxShadow: "8px 0 32px rgba(28,16,61,.12)",
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: compactDrawerWidth,
    [theme.breakpoints.up("sm")]: {
      width: compactDrawerWidth,
    },
  },
  appBarSpacer: {
    minHeight: "52px",
  },
  content: {
    flex: 1,
    overflow: "auto",
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  switch: {
    transform: "scale(0.8)",
  },
  iconButton: {
    color: theme.palette.text.primary,
  },
  themeSwitchContainer: {
    display: "flex",
    alignItems: "center",
  },
  themeIcon: {
    color: theme.palette.text.primary,
  },
  themeMenuTitle: {
    fontWeight: 700,
    pointerEvents: "none",
  },
  themeColors: {
    display: "flex",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2, 1.5),
  },
  colorSwatch: {
    width: 28,
    height: 28,
    minWidth: 28,
    borderRadius: "50%",
    padding: 0,
    border: "2px solid transparent",
  },
  colorSwatchActive: {
    borderColor: theme.palette.text.primary,
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
  },
  themeCard: {
    width: "100%",
    display: "block",
    padding: theme.spacing(1.5),
    textAlign: "left",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    transition: "transform 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[4],
    },
  },
  themeCardActive: {
    border: `2px solid ${theme.palette.primary.main}`,
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}22`,
  },
  themePreview: {
    height: 80,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1.25),
    overflow: "hidden",
    position: "relative",
    padding: theme.spacing(1),
  },
  previewSearch: {
    height: 16,
    borderRadius: 12,
    width: "72%",
    background: "rgba(255,255,255,.16)",
  },
  previewButton: {
    position: "absolute",
    right: 10,
    top: 10,
    width: 28,
    height: 28,
    borderRadius: "50%",
  },
  previewAccent: {
    height: 7,
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
  },
  themeDialogHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  paletteDots: {
    display: "inline-flex",
    gap: 5,
    marginLeft: theme.spacing(1),
    verticalAlign: "middle",
  },
  paletteDot: {
    display: "inline-block",
    width: 13,
    height: 13,
    borderRadius: "50%",
  },
  navList: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    padding: "4px 0 12px",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
    "& .MuiDivider-root": { backgroundColor: "rgba(255,255,255,.1)", margin: "10px 16px" },
    "& .MuiListSubheader-root": { background: "transparent", color: "rgba(255,255,255,.44)" },
  },
  compactToolbar: { justifyContent: "center", padding: "12px 0" },
  collapseButton: {
    color: "rgba(255,255,255,.72)",
    backgroundColor: "rgba(255,255,255,.07)",
    "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,.13)" },
  },
  userDock: {
    margin: "8px 12px 14px",
    padding: "10px",
    minHeight: 58,
    borderRadius: 16,
    display: "flex",
    alignItems: "center",
    gap: 10,
    color: "#fff",
    backgroundColor: "rgba(255,255,255,.07)",
    border: "1px solid rgba(255,255,255,.08)",
    cursor: "pointer",
  },
  userDockCollapsed: { width: 54, height: 54, minHeight: 54, margin: "8px auto 14px", padding: 7, justifyContent: "center" },
  userMeta: { minWidth: 0, flex: 1 },
  userName: { fontWeight: 750, fontSize: ".82rem", color: "#fff" },
  userRole: { fontSize: ".7rem", color: "rgba(255,255,255,.58)" },
}));

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("crm-navigation-expanded") === "true";
  });
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const { darkMode, toggleTheme, activeTheme, setThemePreset, themePresets } = useThemeContext();

  useEffect(() => {
    if (typeof window !== "undefined" && document.body.offsetWidth >= 600) {
      window.localStorage.setItem("crm-navigation-expanded", String(drawerOpen));
    }
  }, [drawerOpen]);

  useEffect(() => {
    const openThemePicker = () => setThemeDialogOpen(true);
    window.addEventListener("crm:open-theme-picker", openThemePicker);
    return () => window.removeEventListener("crm:open-theme-picker", openThemePicker);
  }, []);

  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
    }
  }, [drawerOpen]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false);
    }
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      <Drawer
        variant={drawerVariant}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <div className={clsx(classes.toolbarIcon, !drawerOpen && classes.compactToolbar)}>
          <div className={classes.brand}>
            <div className={classes.brandMark}><BrandOrbitIcon size={44} /></div>
            {drawerOpen && <Typography className={classes.brandText}>La Buena Vibra</Typography>}
          </div>
          {drawerOpen && <IconButton className={classes.collapseButton} onClick={() => setDrawerOpen(false)} aria-label="Contraer navegación">
            <ChevronLeftIcon />
          </IconButton>}
        </div>
        <List className={classes.navList}>
          <MainListItems drawerClose={drawerClose} collapsed={!drawerOpen} />
        </List>
        <Tooltip title={drawerOpen ? "Abrir perfil" : user?.name || "Perfil"} placement="right" arrow>
          <div className={clsx(classes.userDock, !drawerOpen && classes.userDockCollapsed)} onClick={handleOpenUserModal} role="button" tabIndex={0}>
            <Badge color="primary" overlap="circular" variant="dot" anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
              <Avatar style={{ width: 38, height: 38, background: "linear-gradient(135deg,#765cff,#cf7ee8)" }}>{user?.name?.charAt(0) || "U"}</Avatar>
            </Badge>
            {drawerOpen && <div className={classes.userMeta}>
              <Typography noWrap className={classes.userName}>{user?.name || "Usuario"}</Typography>
              <Typography noWrap className={classes.userRole}>{user?.profile === "admin" ? "Administrador" : "Agente"}</Typography>
            </div>}
          </div>
        </Tooltip>
      </Drawer>
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />
      <AppBar
        position="absolute"
        className={clsx(
          classes.appBar,
          drawerOpen ? classes.appBarShift : classes.appBarCompact
        )}
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          <IconButton
            edge="start"
            aria-label="open drawer"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className={clsx(
              classes.menuButton,
              drawerOpen && classes.menuButtonHidden
            )}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            component="h1"
            variant="h6"
            noWrap
            className={classes.title}
          >
            La Buena Vibra CRM
          </Typography>

          <div className={classes.themeSwitchContainer}>
            <Brightness4Icon className={classes.themeIcon} />
            <Switch
              checked={darkMode}
              onChange={toggleTheme}
              color="default"
              className={classes.switch}
            />
          </div>

          {user.id && (
            <NotificationsPopOver className={classes.iconButton} />
          )}

          <div>
            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              className={classes.iconButton}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleOpenUserModal}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <MenuItem
                onClick={() => setThemeDialogOpen(true)}
              >
                <PaletteOutlinedIcon style={{ marginRight: 10 }} />
                Tema de color
              </MenuItem>
              <MenuItem onClick={toggleTheme}>
                <Brightness4Icon style={{ marginRight: 10 }} />
                {darkMode ? "Modo claro" : "Modo oscuro"}
              </MenuItem>
              <MenuItem onClick={handleClickLogout}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <Dialog
        open={themeDialogOpen}
        onClose={() => setThemeDialogOpen(false)}
        maxWidth="md"
        fullWidth
        aria-labelledby="theme-dialog-title"
      >
        <DialogTitle id="theme-dialog-title" disableTypography>
          <div className={classes.themeDialogHeader}>
            <div>
              <Typography variant="h5" component="h2">Selecciona un tema</Typography>
              <Typography color="textSecondary">
                Selecciona un tema para aplicar a toda tu cuenta.
              </Typography>
            </div>
            <IconButton aria-label="Cerrar selector de temas" onClick={() => setThemeDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {themePresets.map((preset) => (
              <Grid item xs={12} sm={6} key={preset.id}>
                <Paper
                  component={ButtonBase}
                  onClick={() => setThemePreset(preset.id)}
                  className={clsx(
                    classes.themeCard,
                    activeTheme.id === preset.id && classes.themeCardActive
                  )}
                  aria-label={`Elegir tema ${preset.name}`}
                >
                  <div
                    className={classes.themePreview}
                    style={{ background: preset.darkSurface }}
                  >
                    <div className={classes.previewSearch} />
                    <div
                      className={classes.previewButton}
                      style={{ background: preset.primary }}
                    />
                    <div
                      className={classes.previewAccent}
                      style={{
                        background: `linear-gradient(90deg, ${preset.primary}, ${preset.secondary})`,
                      }}
                    />
                  </div>
                  <Typography variant="h6" component="div">
                    {preset.name}
                    <span className={classes.paletteDots} aria-hidden="true">
                      <span className={classes.paletteDot} style={{ background: preset.primary }} />
                      <span className={classes.paletteDot} style={{ background: preset.secondary }} />
                      <span className={classes.paletteDot} style={{ background: preset.darkSurface }} />
                    </span>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {preset.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        {children ? children : null}
      </main>
    </div>
  );
};

export default LoggedInLayout;
